/* eslint-disable */
import React, { useRef, useEffect, useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { salmon } from '../../theme';

// #region Helper Components for Camera
export const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const Container = styled.div`
  width: 100%;
  ${({ aspectRatio }) =>
    aspectRatio === 'cover'
      ? `
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    right: 0;`
      : `
    position: relative;
    padding-bottom: ${100 / aspectRatio}%;`}
`;

export const ErrorMsg = styled.div`
  padding: 0px;
  height: 100%;
`;

export const Cam = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  transform: rotateY(${({ mirrored }) => (mirrored ? '180deg' : '0deg')});
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

export const Canvas = styled.canvas`
  display: none;
`;

// #endregion

const handleSuccess = (stream, setNumberOfCameras) => {
  navigator.mediaDevices
    .enumerateDevices()
    .then(r => setNumberOfCameras(r.filter(i => i.kind === 'videoinput').length));

  return stream;
};

const handleError = (error, setNotSupported, setPermissionDenied) => {
  console.error(error);

  //https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
  if (error.name === 'PermissionDeniedError') {
    setPermissionDenied(true);
  } else {
    setNotSupported(true);
  }
};

const initCameraStream = (stream, setStream, currentFacingMode, setNumberOfCameras, setNotSupported, setPermissionDenied, props) => {
  // stop any active streams in the window
  if (stream) {
    stream.getTracks().forEach(track => {
      track.stop();
    });
  }

  const constraints = {
    audio: false,
    video: {
      facingMode: currentFacingMode,
      width: { ideal: 1920 },
      height: { ideal: 1920 },
    },
  };

  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        setStream(handleSuccess(stream, setNumberOfCameras));
      })
      .catch(err => {
        console.log('Position 1');
        handleError(err, setNotSupported, setPermissionDenied);
        props.fixError();
      });
  } else {
    const getWebcam =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;
    if (getWebcam) {
      getWebcam(
        constraints,
        stream => {
          setStream(handleSuccess(stream, setNumberOfCameras));
        },
        err => {
          console.log('Permission denied!');
          handleError(err, setNotSupported, setPermissionDenied);
        },
      );
    } else {
      console.log('Setting  .... not supported!');
      setNotSupported(true);
    }
  }
};

const QuantuxCam = React.forwardRef((props, ref) => {
  const {
    facingMode = 'user',
    aspectRatio = 'cover',
    numberOfCamerasCallback = () => null,
    errorMessages = {
      noCameraAccessible: 'No camera device accessible. Please connect your camera or try a different browser.',
      permissionDenied: 'Permission denied. Please refresh and give camera permission.',
      switchCamera:
        'It is not possible to switch camera to different one because there is only one video device accessible.',
      canvas: 'Canvas is not supported.',
    }
  } = props;

  const player = useRef(null);
  const canvas = useRef(null);
  const container = useRef(null);

  const [numberOfCameras, setNumberOfCameras] = useState(0);
  const [stream, setStream] = useState(null);
  const [currentFacingMode, setFacingMode] = useState(facingMode);
  const [notSupported, setNotSupported] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    numberOfCamerasCallback(numberOfCameras);
  }, [numberOfCameras]);

  useImperativeHandle(ref, () => ({
    takePhoto: () => {
      if (numberOfCameras < 1) {
        throw new Error(errorMessages.noCameraAccessible);
      }

      if (canvas && canvas.current) {
        const playerWidth = player && player.current && player.current.videoWidth || 1280;
        const playerHeight = player && player.current && player.current.videoHeight || 720;
        const playerAR = playerWidth / playerHeight;

        const canvasWidth = container && container.current && container.current.offsetWidth || 1280;
        const canvasHeight = container && container.current && container.current.offsetHeight || 1280;
        const canvasAR = canvasWidth / canvasHeight;

        let sX, sY, sW, sH;

        if (playerAR > canvasAR) {
          sH = playerHeight;
          sW = playerHeight * canvasAR;
          sX = (playerWidth - sW) / 2;
          sY = 0;
        } else {
          sW = playerWidth;
          sH = playerWidth / canvasAR;
          sX = 0;
          sY = (playerHeight - sH) / 2;
        }

        // Reduce resolution
        // const targetWidth = 1600; // Adjust as needed
        // const scaleFactor = targetWidth / sW;
        // canvas.current.width = targetWidth;
        // canvas.current.height = sH * scaleFactor;
        canvas.current.width = sW;
        canvas.current.height = sH;

        const context = canvas.current.getContext('2d');
        if (context && player && player.current) {
          context.drawImage(player.current, sX, sY, sW, sH, 0, 0, sW, sH);
        }

        // const imgData = canvas.current.toDataURL('image/jpeg');
        // Reduce image quality to lower file size
        const imgData = canvas.current.toDataURL('image/jpeg', 0.5);
        return imgData;
      } else {
        throw new Error(errorMessages.canvas);
      }
    },
    switchCamera: () => {
      if (numberOfCameras < 1) {
        throw new Error(errorMessages.noCameraAccessible);
      } else if (numberOfCameras < 2) {
        console.error('Error: Unable to switch camera. Only one device is accessible.'); // console only
      }
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      setFacingMode(newFacingMode);
      return newFacingMode;
    },
    getNumberOfCameras: () => {
      return numberOfCameras;
    },
  }));

  useEffect(() => {
    initCameraStream(stream, setStream, currentFacingMode, setNumberOfCameras, setNotSupported, setPermissionDenied, props);
  }, [currentFacingMode]);

  useEffect(() => {
    if (stream && player && player.current) {
      player.current.srcObject = stream;
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [stream]);

  return (
    <Container ref={container} aspectRatio={aspectRatio}>
      <Wrapper>
        {notSupported
          ? <ErrorMsg>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexFlow: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: salmon[500],
                  fontSize: '56px'
                }}
              >
                <div>
                  <i className='fa fa-exclamation-triangle' />
                </div>
                <div
                  style={{
                    padding: '0 8px',
                    fontSize: '24px',
                    color: salmon[700],
                    textAlign: 'center'
                  }}
                >
                  {errorMessages.noCameraAccessible}
                </div>
              </div>
            </ErrorMsg>
          : null
        }
        {permissionDenied
          ? <ErrorMsg>
              <React.Fragment>
                {errorMessages.permissionDenied}
                <div
                  btnType='primary'
                  onClick={() => localStorage.reload()}
                >
                  Refresh
                </div>
              </React.Fragment>

            </ErrorMsg>
          : null
        }
        <Cam
          ref={player}
          id="video"
          muted={true}
          autoPlay={true}
          playsInline={true}
          mirrored={currentFacingMode === 'user' ? true : false}
          show={!(notSupported || permissionDenied)}
        />
        <Canvas ref={canvas} />
      </Wrapper>
    </Container>
  );
});

export default QuantuxCam;
